import { computed, onMounted, ref } from 'vue'

interface Toast {
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  title: string
  duration?: number
}

const toastQueue = ref<Toast[]>([])
const visible = ref(false)
const currentToast = ref<Toast | null>(null)
let icons: Record<string, any> = {}

export function useToast() {
  // Hide the current toast
  const hideToast = (): void => {
    visible.value = false
    setTimeout(() => {
      currentToast.value = null
      showNextToast()
    }, 300)
  }

  // Show next toast
  const showNextToast = (): void => {
    if (!visible.value && toastQueue.value.length > 0) {
      currentToast.value = toastQueue.value.shift() || null
      visible.value = true

      setTimeout(() => {
        if (visible.value) {
          hideToast()
        }
      }, currentToast.value?.duration || 3000)
    }
  }

  // Load lucide icons
  const loadIcons = async (): Promise<void> => {
    if (Object.keys(icons).length === 0) {
      try {
        const lucide = await import('lucide-vue-next')
        icons = {
          success: lucide.CheckCircleIcon,
          error: lucide.XCircleIcon,
          warning: lucide.AlertTriangleIcon,
          info: lucide.InfoIcon,
        }
      } catch (error) {
        console.error('Failed to load icons:', error)
        // Fallback simple icons
        icons = {
          success: { template: '<div>✓</div>' },
          error: { template: '<div>✕</div>' },
          warning: { template: '<div>⚠</div>' },
          info: { template: '<div>ℹ</div>' },
        }
      }
    }
  }

  onMounted(() => {
    loadIcons()
  })

  // Compute icon
  const icon = computed(() => {
    const type = currentToast.value?.type || 'info'
    return icons[type] || icons.info
  })

  // Compute toast class
  const toastClass = computed(() => {
    if (!currentToast.value) return ''

    switch (currentToast.value.type) {
      case 'success':
        return 'bg-green-100 text-green-800 border-l-4 border-green-500'
      case 'error':
        return 'bg-red-100 text-red-800 border-l-4 border-red-500'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500'
      default:
        return 'bg-blue-100 text-blue-800 border-l-4 border-blue-500'
    }
  })

  // Add toast
  const add = (toast: Toast): void => {
    toastQueue.value.push(toast)
    if (!visible.value) {
      showNextToast()
    }
  }

  // Convenience methods
  const success = (message: string, title: string = 'Success', duration: number = 3000): void =>
    add({ type: 'success', message, title, duration })

  const error = (message: string, title: string = 'Error', duration: number = 3000): void =>
    add({ type: 'error', message, title, duration })

  const warn = (message: string, title: string = 'Warning', duration: number = 3000): void =>
    add({ type: 'warning', message, title, duration })

  const info = (message: string, title: string = 'Info', duration: number = 3000): void =>
    add({ type: 'info', message, title, duration })

  return {
    visible: computed(() => visible.value),
    icon,
    toastClass,
    hideToast,
    add,
    success,
    error,
    warn,
    info,
    currentToast: computed(() => currentToast.value || { title: '', message: '', type: 'info' }),
  }
}
